/**
 * RESEND BPI LINK DIALOG
 * Dialog modal de renvoi manuel du mail de paiement BPI (TICKET-CRM-10).
 *
 * Parcours :
 *   1. L'admin vérifie le destinataire résolu (payeur identifié
 *      payer_contact_id du dossier, sinon pilote/contact d'inscription —
 *      mêmes candidats que BedLinkDialog via list_payment_case_contacts_for_staff)
 *      et choisit la langue de l'email (fr/en/es).
 *   2. Submit -> Edge Function send-bpi-link avec { payment_case_id, locale }
 *      UNIQUEMENT — depuis TICKET-BPI-SEC-01 le destinataire est résolu
 *      SERVEUR (payeur identifié sinon pilote, même règle que l'aperçu
 *      ci-dessous) -> route interne 4LT /api/internal/send-bpi-link
 *      (Resend + React Email + next-intl). La route 4LT choisit elle-même le
 *      template selon le statut du dossier et insère l'event timeline
 *      payment_link_resent (migration 00134) — le CRM n'écrit rien.
 *
 * NB idempotence : côté 4LT l'envoi Resend utilise une fenêtre d'idempotence
 * de 24 h — un re-clic le même jour répond 200 sans renvoyer de mail. Pas de
 * gestion particulière côté UI (le toast succès reste honnête : le mail du
 * jour est bien parti).
 *
 * Modèle : BedLinkDialog (RHF + Zod, Select locale, toast Sonner).
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Form, FormSelect, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useSupabase } from '../../hooks/useSupabase';
import { usePaymentCaseContacts } from '../../hooks/usePaymentCaseContacts';
import type { PayerCandidate } from '../../hooks/usePaymentCaseContacts';

// Langues supportées par les templates email BPI côté 4LT (Resend + next-intl).
const EMAIL_LOCALES = [
  { value: 'fr', label: 'Français' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

const resendBpiLinkSchema = z.object({
  locale: z.enum(['fr', 'en', 'es']),
});

type ResendBpiLinkFormValues = z.infer<typeof resendBpiLinkSchema>;

interface ResendBpiLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Dossier BPI non-terminal dont on renvoie le mail de paiement.
  paymentCaseId: string;
  // Payeur identifié sur le dossier (payment_cases.payer_contact_id) :
  // destinataire prioritaire s'il est renseigné.
  payerContactId: string | null;
  prospectName: string;
  productName: string;
}

function formatCandidateName(candidate: PayerCandidate): string {
  return `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim() || 'Contact';
}

// Aperçu du destinataire — AFFICHAGE uniquement : depuis TICKET-BPI-SEC-01
// l'autorité est l'Edge Function, qui applique la même règle côté serveur
// (payeur identifié sur le dossier s'il existe, sinon pilote, sinon l'unique
// candidat). Sert aussi à désactiver le bouton quand aucun candidat.
function resolveRecipientContactId(
  payerContactId: string | null,
  candidates: PayerCandidate[]
): string | undefined {
  if (payerContactId) return payerContactId;
  if (candidates.length === 0) return undefined;
  const pilot = candidates.find((c) => c.role === 'pilot');
  return (pilot ?? candidates[0]).contact_id;
}

export function ResendBpiLinkDialog({
  open,
  onOpenChange,
  paymentCaseId,
  payerContactId,
  prospectName,
  productName,
}: ResendBpiLinkDialogProps) {
  const supabase = useSupabase();
  const [sending, setSending] = useState(false);

  const {
    candidates,
    loading: loadingCandidates,
    error: candidatesError,
  } = usePaymentCaseContacts({
    paymentCaseId: open ? paymentCaseId : undefined,
  });

  const form = useForm<ResendBpiLinkFormValues>({
    resolver: zodResolver(resendBpiLinkSchema),
    defaultValues: { locale: 'fr' },
  });

  // Réinitialise l'état à chaque ouverture (le dialog est monté en permanence).
  useEffect(() => {
    if (open) {
      form.reset({ locale: 'fr' });
    }
  }, [open, form]);

  const recipientContactId = useMemo(
    () => resolveRecipientContactId(payerContactId, candidates),
    [payerContactId, candidates]
  );

  // Affichage du destinataire : nom du candidat correspondant si dispo. Le
  // payeur identifié fait normalement partie des candidats (ADR D11,
  // contact_not_allowed) ; sinon fallback générique, l'email réel est de
  // toute façon relu en DB au moment de l'envoi.
  const recipientLabel = useMemo(() => {
    if (!recipientContactId) return null;
    const candidate = candidates.find((c) => c.contact_id === recipientContactId);
    if (!candidate) return 'Payeur identifié';
    const name = formatCandidateName(candidate);
    if (candidate.contact_id === payerContactId) return `${name} (payeur)`;
    if (candidate.role === 'pilot') return `${name} (pilote)`;
    if (candidate.role === 'copilot') return `${name} (copilote)`;
    return name;
  }, [recipientContactId, candidates, payerContactId]);

  async function handleSend(values: ResendBpiLinkFormValues) {
    if (!recipientContactId) {
      toast.error('Aucun destinataire identifié pour ce dossier.');
      return;
    }

    setSending(true);
    try {
      // Le navigateur n'envoie plus que le dossier et la langue : email et
      // nom du destinataire sont résolus SERVEUR par l'Edge Function
      // (TICKET-BPI-SEC-01). L'email retourné sert au toast de confirmation.
      const { data, error: invokeError } = await supabase.functions.invoke('send-bpi-link', {
        body: {
          payment_case_id: paymentCaseId,
          locale: values.locale,
        },
      });

      if (invokeError) throw invokeError;

      const recipientEmail = (data as { recipient_email?: string } | null)?.recipient_email;
      toast.success(
        recipientEmail
          ? `Mail de paiement renvoyé à ${recipientEmail}`
          : 'Mail de paiement renvoyé au participant'
      );
      onOpenChange(false);
    } catch (err) {
      console.error('[ResendBpiLinkDialog] send-bpi-link error:', err);
      toast.error("Erreur lors de l'envoi du mail de paiement.");
    } finally {
      setSending(false);
    }
  }

  function handleClose(value: boolean) {
    if (sending) return;
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Renvoyer le mail de paiement</DialogTitle>
          <DialogDescription>
            Acompte BPI — <strong>{prospectName}</strong> — {productName}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSend)} className="flex flex-col gap-4">
            {loadingCandidates ? (
              <p className="text-xs text-zinc-400">Chargement des contacts...</p>
            ) : candidatesError ? (
              <p className="text-xs text-red-500">{candidatesError}</p>
            ) : recipientLabel ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium text-zinc-700">Destinataire</span>
                <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
                  {recipientLabel}
                </div>
              </div>
            ) : (
              <p className="text-xs text-red-500">Aucun destinataire identifié pour ce dossier.</p>
            )}

            <FormSelect
              control={form.control}
              name="locale"
              label="Langue de l'email"
              options={EMAIL_LOCALES.map((l) => ({ value: l.value, label: l.label }))}
              required
              disabled={sending}
            />

            <p className="text-xs text-zinc-400">
              Le contenu du mail est choisi automatiquement selon le statut du dossier (choix de
              paiement, attente virement ou chèque).
            </p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={sending}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={sending || loadingCandidates || !recipientContactId}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <Send className="mr-2 size-4" />
                {sending ? 'Envoi...' : 'Envoyer'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
